import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import Drawer from "@/components/Drawer";
import { ClockClockwise, ArrowRight } from "@phosphor-icons/react";

function PriceDeltaPill({ before, after }) {
  if (before == null || after == null) {
    return (
      <span className="font-mono text-[11px] text-ink-muted">
        {before == null ? "new" : "removed"}
      </span>
    );
  }
  const delta = after - before;
  if (delta === 0) return null;
  const positive = delta > 0;
  return (
    <span
      className={`font-mono text-[10px] px-1.5 py-0.5 ${
        positive
          ? "text-destructive bg-destructive/10"
          : "text-[#00B341] bg-[#00B341]/10"
      }`}
    >
      {positive ? "+" : ""}
      {fmtMoney(delta)}
    </span>
  );
}

function HistoryEntry({ entry }) {
  const oldP = entry.old_price ?? 0;
  const newP = entry.new_price ?? 0;
  const diffs = entry.item_diffs || [];
  const isCsv = entry.source === "csv_import";
  return (
    <li
      data-testid={`history-entry-${entry.id}`}
      className="border-b border-canvas-line py-4 last:border-b-0"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-mono text-[11px] text-ink-muted flex items-center gap-2 flex-wrap">
          {entry.effective_date && (
            <span
              className="px-1.5 py-0.5 bg-ink text-white text-[10px] uppercase tracking-wider"
              data-testid={`effective-date-${entry.id}`}
            >
              eff. {entry.effective_date}
            </span>
          )}
          <span>
            {fmtDate(entry.changed_at)} · by {entry.changed_by_name || entry.changed_by || "Unknown"}
          </span>
          {isCsv && (
            <span className="font-mono text-[9px] uppercase tracking-wider text-ink-subtle border border-canvas-line px-1.5 py-0.5">
              CSV import
            </span>
          )}
        </div>
        <PriceDeltaPill before={oldP} after={newP} />
      </div>
      <div className="flex items-center gap-2 mb-3 text-sm">
        <span className="font-mono text-ink-muted line-through">{fmtMoney(oldP)}</span>
        <ArrowRight size={11} weight="bold" className="text-ink-muted" />
        <span className="font-mono font-bold text-ink">{fmtMoney(newP)}</span>
      </div>
      {diffs.length > 0 && (
        <ul className="space-y-1 pl-3 border-l border-canvas-line">
          {diffs.map((d, idx) => (
            <li
              key={`${d.label}-${idx}`}
              className="flex items-center justify-between text-xs"
            >
              <span className="text-ink truncate pr-2">{d.label}</span>
              <span className="flex items-center gap-1.5 font-mono shrink-0">
                <span className="text-ink-muted line-through">
                  {d.before == null ? "—" : fmtMoney(d.before)}
                </span>
                <ArrowRight size={9} weight="bold" className="text-ink-muted" />
                <span className="text-ink font-semibold">
                  {d.after == null ? "—" : fmtMoney(d.after)}
                </span>
                <PriceDeltaPill before={d.before} after={d.after} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function PackageHistoryDrawer({ open, pkg, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !pkg) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/packages/${pkg.id}/history`)
      .then((r) => {
        if (!cancelled) setEntries(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pkg]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Audit Log"
      title={pkg ? `${pkg.name} — Price History` : "History"}
      testIdClose="close-history-drawer"
    >
      <div data-testid="package-history">
        {loading && (
          <div className="font-mono text-xs text-ink-subtle text-center py-8">
            Loading history…
          </div>
        )}
        {!loading && entries.length === 0 && (
          <div className="text-center py-12">
            <ClockClockwise size={28} weight="bold" className="text-ink-subtle mx-auto mb-2" />
            <div className="font-mono text-xs text-ink-subtle">
              No price changes recorded yet.
            </div>
            <div className="font-mono text-[11px] text-ink-subtle mt-1">
              History tracks every edit to package price or items.
            </div>
          </div>
        )}
        <ul>
          {entries.map((e) => (
            <HistoryEntry key={e.id} entry={e} />
          ))}
        </ul>
      </div>
    </Drawer>
  );
}
